"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { ArrowRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { QuickAssessment } from "@/components/shared/QuickAssessment";
import { TrackingEvents } from "@/lib/analytics";
import { suppressDiagnosticNudge } from "@/lib/diagnostic-nudge";

const trackingContext = {
  placement: "hero",
  cta_variant: "home-hero-dialog-v1",
  page_path: "/",
};

export function HomeAssessmentDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const closeReasonRef = useRef("dismiss");
  const triggerRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      if (!wasOpenRef.current) return undefined;
      wasOpenRef.current = false;
      const frame = window.requestAnimationFrame(() => triggerRef.current?.focus());
      return () => window.cancelAnimationFrame(frame);
    }

    wasOpenRef.current = true;
    document.body.dataset.assessmentDialogOpen = "true";
    return () => {
      delete document.body.dataset.assessmentDialogOpen;
    };
  }, [isOpen]);

  function openDialog() {
    closeReasonRef.current = "dismiss";
    suppressDiagnosticNudge(window);
    setHasOpened(true);
    setIsOpen(true);
    TrackingEvents.trackHomeAssessmentStep("dialog_open", trackingContext);
  }

  function closeDialog(reason = closeReasonRef.current) {
    if (!isOpen) return;
    setIsOpen(false);
    TrackingEvents.trackHomeAssessmentStep("dialog_close", {
      ...trackingContext,
      reason,
    });
    closeReasonRef.current = "dismiss";
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openDialog}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="home-assessment-dialog"
        className="inline-flex min-h-12 touch-manipulation items-center justify-center rounded-full bg-water-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
      >
        Request Free Assessment
        <ArrowRightIcon className="ml-2 h-4 w-4" aria-hidden="true" />
      </button>

      {hasOpened ? (
        <Dialog
          id="home-assessment-dialog"
          open={isOpen}
          onClose={() => closeDialog()}
          unmount={false}
          aria-describedby="home-assessment-dialog-description"
          className="relative z-[90]"
        >
          <DialogBackdrop className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm" />

          <div className="fixed inset-0 flex w-screen items-end justify-center overflow-y-auto overscroll-contain sm:items-center sm:p-4">
            <DialogPanel className="relative max-h-[calc(100dvh-0.75rem)] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-t-[1.5rem] border border-water-border bg-white px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-6 shadow-[0_24px_80px_rgba(11,34,57,0.28)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-[1.5rem] sm:p-8">
              <CloseButton
                type="button"
                onClick={() => {
                  closeReasonRef.current = "close_button";
                }}
                aria-label="Close free assessment form"
                className="absolute right-3 top-3 inline-flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-water-canvas text-water-muted transition-colors hover:bg-water-aqua hover:text-water-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 sm:right-5 sm:top-5"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </CloseButton>

              <div className="pr-12">
                <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue">
                  Free Home Assessment
                </p>
                <DialogTitle className="mt-3 text-balance text-2xl font-semibold tracking-[-0.025em] text-water-ink sm:text-3xl">
                  Get a system recommendation before you buy
                </DialogTitle>
                <p id="home-assessment-dialog-description" className="mt-3 text-pretty text-base leading-7 text-water-muted">
                  Share your first name and phone number. We’ll call to confirm your needs and, when appropriate, arrange a visit. This request is not a booked visit or final quote.
                </p>
              </div>

              <div className="mt-6 border-t border-water-border pt-6">
                <QuickAssessment
                  mode="residential"
                  service="home-system-recommendation"
                  pagePath="/"
                  placement="hero"
                  ctaVariant="home-hero-dialog-v1"
                  funnelVariant="home_hero_dialog"
                  responsiveVerification
                  submitLabel="Request Free Assessment"
                />
              </div>

              <p className="mt-6 border-t border-water-border pt-5 text-sm leading-6 text-water-muted">
                Prefer to add household, address, or installation details?{" "}
                <Link
                  href="/forms/home-assessment"
                  onClick={() => closeDialog("full_assessment_link")}
                  className="font-semibold text-water-blue underline decoration-water-border underline-offset-4 hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                >
                  Open the full assessment
                </Link>
                .
              </p>
            </DialogPanel>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
